import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../api';
import Turnstile from '../components/Turnstile';

// English / Hindi (13 Oct 2026, Hindi version Batch 1b)
import { useI18n } from '../i18n';
// Password box after registration (8 Oct 2026). "Go to dashboard" at the
// end of registration sets this flag; the dashboard then opens dimmed
// behind a box asking for the password, and only the correct password
// clears it. Survives a page refresh (same browser tab).
const CONFIRM_FLAG = 'thyro_confirm_password';

// { id, role } from the access token, or null.
function userFromToken(token) {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=')));
    return json && json.sub ? { id: json.managedBy || json.sub, role: json.role } : null;
  } catch { return null; }
}

const AuthContext = createContext(null);

// Warnings from open screens about something that can't be saved by itself
// and would be lost by logging out (10 Oct 2026) — each screen adds a
// function to window.__thyroLeaveGuards that returns a text or null.
// Passed by the idle / session timers, so logout knows nobody clicked
const AUTO_LOGOUT = { auto: true };

function leaveWarnings() {
  try {
    return Array.from(window.__thyroLeaveGuards || [])
      .map(fn => { try { return fn(); } catch { return null; } })
      .filter(Boolean);
  } catch { return []; }
}

// 15 minutes, per product decision (was 30 min in an earlier draft of this
// requirement — this is the current, final value).
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
// Warning shown this long before the idle logout (27 Sep 2026 — the idle
// logout used to happen with no warning at all). Any activity — moving
// the mouse, a key, a tap, or the "Stay logged in" button — resets it.
const IDLE_WARNING_LEAD_MS = 60 * 1000;
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];

// ─── Fixed-duration session timer — separate mechanism from idle timeout ──
// Starts a fresh countdown at login and does NOT reset on ordinary
// activity (mousemove, scroll, etc.) the way the idle timer above does —
// only an explicit "Continue" click on the warning resets it. This is
// the income-tax-India / government-portal pattern: even an actively-
// working patient gets forced to re-confirm periodically, as a second,
// independent layer on top of (not instead of) the 15-minute idle
// timeout — a patient who's idle for 15 minutes still gets logged out
// by that mechanism first, regardless of where this timer is.
const SESSION_DURATION_MS = 30 * 60 * 1000; // 30 minutes, fixed, from login or last "Continue"
const SESSION_WARNING_LEAD_MS = 30 * 1000;  // show the "Continue?" prompt 30s before expiry

export const AuthProvider = ({ children }) => {
  const { t } = useI18n();
  // Latest t for callbacks that are created once (logout).
  const tRef = useRef(t);
  tRef.current = t;
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const idleTimerRef = useRef(null);
  const idleWarningTimerRef = useRef(null);
  const idleCountdownRef = useRef(null);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [idleSecondsLeft, setIdleSecondsLeft] = useState(60);
  const userRef = useRef(null); // mirrors `user` for use inside the event-listener closures below

  // Fixed-duration session timer state
  const sessionTimerRef = useRef(null);
  const sessionWarningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [warningSecondsLeft, setWarningSecondsLeft] = useState(30);
  // Password box after registration (8 Oct 2026)
  const [needsConfirm, setNeedsConfirm] = useState(false);

  useEffect(() => {
    const token = authAPI.getToken();
    const storedUser = sessionStorage.getItem('thyro_user');
    if (token && sessionStorage.getItem(CONFIRM_FLAG)) {
      // Registration just finished; the password box is still waiting.
      const u = userFromToken(token);
      if (u && u.role === 'patient') { setUser(u); setNeedsConfirm(true); }
      else sessionStorage.removeItem(CONFIRM_FLAG);
    } else if (token && storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch { sessionStorage.clear(); }
    }
    setLoading(false);
  }, []);

  // Called by RegisterPage's "Go to dashboard" (8 Oct 2026): show the
  // dashboard behind the password box, using the registration session.
  const startPasswordConfirm = useCallback(() => {
    const u = userFromToken(authAPI.getToken());
    if (!u) return false;
    sessionStorage.setItem(CONFIRM_FLAG, '1');
    sessionStorage.removeItem('thyro_user');
    setUser(u);
    setNeedsConfirm(true);
    return true;
  }, []);

  // The correct password: a fresh session, exactly as after login.
  const confirmPassword = useCallback(async (password, turnstileToken) => {
    const res = await authAPI.confirmPassword(password, turnstileToken);
    authAPI.setTokens(res.accessToken, res.refreshToken);
    const userData = { id: res.userId, role: res.role };
    sessionStorage.setItem('thyro_user', JSON.stringify(userData));
    sessionStorage.removeItem(CONFIRM_FLAG);
    setUser(userData);
    setNeedsConfirm(false);
    return userData;
  }, []);

  const login = useCallback(async (identifier, password, role, turnstileToken) => {
    const res = await authAPI.login(identifier, password, role, turnstileToken);
    const { accessToken, refreshToken, userId, role: userRole } = res;
    authAPI.setTokens(accessToken, refreshToken);
    const userData = { id: userId, role: userRole };
    sessionStorage.setItem('thyro_user', JSON.stringify(userData));
    sessionStorage.removeItem(CONFIRM_FLAG); // a normal login never needs the post-registration box
    // A landing-page condition choice belongs to a NEW registration only —
    // never carried into someone's ordinary login on a shared PC (audit 8 Oct 2026)
    try { localStorage.removeItem('thyro_locked_condition'); } catch (e) { /* storage blocked */ }
    setNeedsConfirm(false);
    setUser(userData);
    return userData;
  }, []);

  // Hard redirect (not just clearing React state) is deliberate: it
  // guarantees every component fully remounts with fresh data on the next
  // login, rather than any component instance surviving with stale state
  // from the previous session. This also makes logout safe to call from
  // the idle-timer below, which fires outside of any component's render.
  // redirectTo (audit 8 Oct 2026): "Forgot password?" in the
  // Confirm-it's-you box lands on the login page's reset form. Only a
  // string counts — logout is also used directly as a click handler,
  // which passes the click event.
  const logout = useCallback(async (redirectTo) => {
    // Something that can't be saved by itself (an amendment to an online
    // opinion, 10 Oct 2026): a Log out click asks first. The automatic
    // logouts say it in their warning box instead (below).
    if (redirectTo !== AUTO_LOGOUT) {
      const warnings = leaveWarnings();
      if (warnings.length && !window.confirm(`${warnings.map(w => tRef.current(w)).join('\n\n')}\n\n${tRef.current('Log out anyway?')}`)) return;
    }
    // Answers still waiting to be saved (questionnaires, the doctor's
    // opinion) are saved first — at most 3 seconds (audit 9 Oct 2026).
    try {
      const flushers = Array.from(window.__thyroFlushers || []);
      if (flushers.length) {
        await Promise.race([
          Promise.allSettled(flushers.map(fn => Promise.resolve().then(fn))),
          new Promise(r => setTimeout(r, 3000)),
        ]);
      }
    } catch { /* never blocks logout */ }
    try {
      const refreshToken = sessionStorage.getItem('thyro_refresh_token');
      await authAPI.logout(refreshToken);
    } catch { /* silent */ } finally {
      authAPI.clearTokens();
      sessionStorage.removeItem('thyro_user');
      sessionStorage.removeItem(CONFIRM_FLAG);
      try {
        localStorage.removeItem('thyro_locked_condition');
        localStorage.removeItem('thyro_login_channel');
      } catch (e) { /* storage blocked */ }
      window.location.href = (typeof redirectTo === 'string' && redirectTo.startsWith('/login')) ? redirectTo : '/login';
    }
  }, []);

  useEffect(() => { userRef.current = user; }, [user]);

  // ─── 15-minute idle timeout ───────────────────────────────────────────
  useEffect(() => {
    if (!user) return; // only run the idle timer while actually logged in

    const clearIdle = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (idleWarningTimerRef.current) clearTimeout(idleWarningTimerRef.current);
      if (idleCountdownRef.current) clearInterval(idleCountdownRef.current);
    };

    const resetTimer = () => {
      clearIdle();
      setShowIdleWarning(false);
      idleWarningTimerRef.current = setTimeout(() => {
        setIdleSecondsLeft(Math.round(IDLE_WARNING_LEAD_MS / 1000));
        setShowIdleWarning(true);
        idleCountdownRef.current = setInterval(() => {
          setIdleSecondsLeft(s => (s > 0 ? s - 1 : 0));
        }, 1000);
      }, IDLE_TIMEOUT_MS - IDLE_WARNING_LEAD_MS);
      idleTimerRef.current = setTimeout(() => {
        if (userRef.current) logout(AUTO_LOGOUT);
      }, IDLE_TIMEOUT_MS);
    };

    resetTimer();
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, resetTimer));

    return () => {
      clearIdle();
      setShowIdleWarning(false);
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, resetTimer));
    };
  }, [user, logout]);

  // ─── 30-minute fixed session timer + "continue?" warning ──────────────
  // Deliberately does NOT listen to ACTIVITY_EVENTS — unlike the idle
  // timer above, ordinary use of the app does not reset this one. Only
  // an explicit click on "Continue" in the warning modal (continueSession
  // below) restarts the 30-minute window. This is intentional: it forces
  // periodic re-confirmation even for a continuously active patient,
  // as a second, independent layer over the idle timeout.
  const clearSessionTimers = useCallback(() => {
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    if (sessionWarningTimerRef.current) clearTimeout(sessionWarningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  }, []);

  const scheduleSessionTimers = useCallback(() => {
    clearSessionTimers();
    setShowSessionWarning(false);

    sessionWarningTimerRef.current = setTimeout(() => {
      setWarningSecondsLeft(Math.round(SESSION_WARNING_LEAD_MS / 1000));
      setShowSessionWarning(true);
      // Live countdown shown in the modal — matches the
      // income-tax-India-style pattern of a visibly ticking warning.
      countdownIntervalRef.current = setInterval(() => {
        setWarningSecondsLeft(s => (s > 0 ? s - 1 : 0));
      }, 1000);
    }, SESSION_DURATION_MS - SESSION_WARNING_LEAD_MS);

    sessionTimerRef.current = setTimeout(() => {
      if (userRef.current) logout(AUTO_LOGOUT);
    }, SESSION_DURATION_MS);
  }, [clearSessionTimers, logout]);

  // "Continue" button in the warning modal — also proactively refreshes
  // the access token (not just restarting this timer), so the JWT itself
  // doesn't independently expire out from under a session the patient
  // just explicitly chose to keep.
  const continueSession = useCallback(() => {
    // refreshSession saves the new token pair — the server cancels the old
    // refresh token on every use (audit 8 Oct 2026).
    if (sessionStorage.getItem('thyro_refresh_token')) authAPI.refreshSession().catch(() => {});
    scheduleSessionTimers();
  }, [scheduleSessionTimers]);

  useEffect(() => {
    if (!user) { clearSessionTimers(); setShowSessionWarning(false); return; }
    scheduleSessionTimers();
    return clearSessionTimers;
  }, [user, scheduleSessionTimers, clearSessionTimers]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, startPasswordConfirm, needsConfirm }}>
      {children}
      {needsConfirm && <ConfirmPasswordBox onConfirm={confirmPassword} onLogout={logout} />}
      {/* Idle warning (27 Sep 2026). Hidden while the 30-minute session
          warning is showing — that one takes priority. Any activity closes
          it, because activity resets the idle timer. */}
      {showIdleWarning && !showSessionWarning && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div style={{
            background: 'var(--surface)', borderRadius: 'var(--radius-lg)', padding: 28,
            width: '100%', maxWidth: 380, minHeight: 190, textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
          }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>💤</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>
              {t('Are you still there?')}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              {t('You\'ve been inactive for a while. For your privacy you\'ll be logged out in')}{' '}
              <strong style={{ display: 'inline-block', minWidth: '2ch', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                {idleSecondsLeft}
              </strong>{' '}
              {idleSecondsLeft === 1 ? t('second. Your saved answers are kept.') : t('seconds. Your saved answers are kept.')}
            </div>
            {leaveWarnings().map(w => (
              <div key={w} style={{ fontSize: 13, color: '#92400e', background: '#fef3c7', borderRadius: 8, padding: '8px 10px', marginBottom: 16 }}>⚠ {t(w)}</div>
            ))}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                type="button"
                onClick={logout}
                style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}
              >
                {t('Log out now')}
              </button>
              <button
                type="button"
                onClick={() => setShowIdleWarning(false)}
                style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: 'var(--teal-400)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                {t('Stay logged in')}
              </button>
            </div>
          </div>
        </div>
      )}
      {showSessionWarning && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div style={{
            background: 'var(--surface)', borderRadius: 'var(--radius-lg)', padding: 28,
            width: '100%', maxWidth: 380, minHeight: 190, textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
          }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>⏱️</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>
              {t('Your session is about to expire')}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              {t('You\'ll be logged out in')}{' '}
              <strong style={{ display: 'inline-block', minWidth: '2ch', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                {warningSecondsLeft}
              </strong>{' '}
              {warningSecondsLeft === 1 ? t('second unless you choose to continue.') : t('seconds unless you choose to continue.')}
            </div>
            {leaveWarnings().map(w => (
              <div key={w} style={{ fontSize: 13, color: '#92400e', background: '#fef3c7', borderRadius: 8, padding: '8px 10px', marginBottom: 16 }}>⚠ {t(w)}</div>
            ))}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                type="button"
                onClick={logout}
                style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}
              >
                {t('Log out now')}
              </button>
              <button
                type="button"
                onClick={continueSession}
                style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: 'var(--teal-400)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                {t('Continue session')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

// ─── Password box after registration (8 Oct 2026) ─────────────────────────
// Covers the whole screen; the page behind is dimmed and blurred and can't
// be used. No close button — only the correct password (or logging out /
// "Forgot password?") leaves it. The Cloudflare check appears after 2
// wrong passwords, as on the login page.
const ConfirmPasswordBox = ({ onConfirm, onLogout }) => {
  const { t } = useI18n();
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [needCaptcha, setNeedCaptcha] = useState(false);
  const [tsToken, setTsToken] = useState('');
  // The check could not load (blocked script, local PC) — let the server
  // decide rather than keep Continue disabled for good (audit 8 Oct 2026).
  const [tsError, setTsError] = useState('');
  const tsRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) { setError('Please enter your password'); return; }
    setBusy(true); setError('');
    try {
      await onConfirm(password, tsToken || undefined);
    } catch (err) {
      const data = err?.response?.data || {};
      if (data.code === 'SESSION_ENDED' || err?.response?.status === 401) {
        setError('Your session has ended. Please log in with your mobile number or email and password.');
      } else {
        setError(data.error || 'Could not check your password. Please try again.');
      }
      if (data.captchaRequired || data.code === 'CAPTCHA_REQUIRED' || data.code === 'CAPTCHA_FAILED') setNeedCaptcha(true);
      setPassword('');
      // A check token works once; clear it with the widget.
      setTsToken('');
      if (tsRef.current) tsRef.current.reset();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2500, background: 'rgba(15,23,42,0.45)',
      backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <form onSubmit={submit} style={{
        background: 'var(--surface)', borderRadius: 'var(--radius-lg)', padding: 28,
        width: '100%', maxWidth: 360, boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontSize: 30, textAlign: 'center', marginBottom: 8 }}>🔐</div>
        <div style={{ fontSize: 16, fontWeight: 600, textAlign: 'center', marginBottom: 6, color: 'var(--text-primary)' }}>
          {t('Confirm it\'s you')}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', marginBottom: 18 }}>
          {t('Please enter your password to open your dashboard.')}
        </div>
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <input
            className="form-input"
            type={show ? 'text' : 'password'}
            value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }}
            placeholder={t('Password')}
            aria-label={t('Password')}
            autoFocus
            autoComplete="current-password"
            style={{ paddingRight: 44, width: '100%', boxSizing: 'border-box' }}
          />
          <button type="button" onClick={() => setShow(s => !s)} aria-label={show ? t('Hide password') : t('Show password')}
            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: 14 }}>
            {show ? '🙈' : '👁'}
          </button>
        </div>
        {needCaptcha && <Turnstile ref={tsRef} onToken={t => { setTsToken(t); setTsError(''); }} onError={c => setTsError(c || 'error')} action="confirm_password" appearance="always" />}
        {error && <div style={{ fontSize: 12, color: '#b91c1c', marginBottom: 10 }}>{t(error)}</div>}
        <button type="submit" disabled={busy || (needCaptcha && !tsToken && !tsError)}
          style={{ width: '100%', padding: '10px 0', borderRadius: 8, border: 'none', background: 'var(--teal-400)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}>
          {busy ? t('Checking…') : t('Continue')}
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, fontSize: 12 }}>
          <button type="button" onClick={onLogout} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-tertiary)' }}>
            {t('Log out')}
          </button>
          <button type="button" onClick={() => onLogout('/login?forgot=1')} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--teal-600)', fontWeight: 500 }}>
            {t('Forgot password?')}
          </button>
        </div>
      </form>
    </div>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
